(function () {
  'use strict';

  // Configuration injected by sections/product-bundle.liquid
  const KIT = window.BundleKit || {};
  const STRINGS = KIT.strings || {};

  // State
  let selectedSize = null;
  let selectedBundleVariantId = null;
  const selectedVariants = {};
  const selectedQuantities = {};
  const productCache = {};

  // Format a price (in cents) using shop.money_format from Liquid.
  // Supports the standard Shopify placeholders.
  function formatMoney(cents) {
    const format = KIT.moneyFormat || '${{amount}}';
    const amount = (cents || 0) / 100;

    function withDelimiters(value, precision = 2, thousands = ',', decimal = '.') {
      const parts = value.toFixed(precision).split('.');
      const intPart = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const decPart = parts[1] ? decimal + parts[1] : '';
      return intPart + decPart;
    }

    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, placeholder) => {
      switch (placeholder) {
        case 'amount': return withDelimiters(amount, 2, ',', '.');
        case 'amount_no_decimals': return withDelimiters(amount, 0, ',');
        case 'amount_with_comma_separator': return withDelimiters(amount, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelimiters(amount, 0, '.');
        case 'amount_with_apostrophe_separator': return withDelimiters(amount, 2, "'", '.');
        case 'amount_no_decimals_with_space_separator': return withDelimiters(amount, 0, ' ');
        case 'amount_with_space_separator': return withDelimiters(amount, 2, ' ', ',');
        case 'amount_with_period_and_space_separator': return withDelimiters(amount, 2, ' ', '.');
        default: return '';
      }
    });
  }

  // Show or clear the inline error message below the add-to-cart button
  function showError(message) {
    let errEl = document.getElementById('bundle-error');
    if (!errEl) {
      const btn = document.getElementById('bundle-add-to-cart');
      if (!btn) return;
      errEl = document.createElement('p');
      errEl.id = 'bundle-error';
      errEl.className = 'product-bundle__error';
      errEl.setAttribute('role', 'alert');
      btn.parentNode.insertBefore(errEl, btn.nextSibling);
    }
    errEl.textContent = message || '';
    errEl.style.display = message ? '' : 'none';
  }

  // Fetch product data via AJAX
  async function fetchProduct(handle) {
    if (productCache[handle]) return productCache[handle];
    const res = await fetch(`/products/${handle}.js`);
    if (!res.ok) throw new Error(`Could not fetch product: ${handle}`);
    const data = await res.json();
    productCache[handle] = data;
    return data;
  }

  // Check if a variant has enough inventory for the required qty
  function hasEnoughStock(variantId, requiredQty) {
    const inv = KIT.inventory ? KIT.inventory[variantId] : null;
    if (!inv) return true; // no data — assume ok
    if (inv.policy === 'continue') return true; // overselling allowed
    return inv.qty >= requiredQty;
  }

  // Render color swatches for a component
  function renderSwatches(variants, containerId, componentKey) {
    const container = document.getElementById(containerId);
    if (!container) return;
    container.innerHTML = '';

    const defaultVariantTitle = KIT.defaultSelections
      ? KIT.defaultSelections[componentKey]
      : null;

    const requiredQty = selectedQuantities[componentKey] || 0;
    let hasPreselected = false;

    variants.forEach((variant) => {
      const isUnavailable = !variant.available ||
        (requiredQty > 0 && !hasEnoughStock(variant.id, requiredQty));

      if (isUnavailable) return;

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'swatch-btn swatch-btn--circle';
      btn.dataset.variantId = variant.id;
      btn.dataset.variantTitle = variant.title;
      btn.dataset.price = variant.price;
      btn.dataset.componentKey = componentKey;

      const tooltipText = variant._product_title
        ? `${variant._product_title} - ${variant.title}`
        : variant.title;
      btn.title = tooltipText;
      btn.setAttribute('aria-label', tooltipText);
      btn.dataset.displayLabel = tooltipText;

      const swatchData = KIT.swatches ? KIT.swatches[variant.title] : null;
      if (swatchData && swatchData.image) {
        const img = document.createElement('img');
        img.src = swatchData.image;
        img.alt = tooltipText;
        img.className = 'swatch-btn__image';
        img.loading = 'lazy';
        btn.appendChild(img);
      } else if (swatchData && swatchData.color) {
        btn.style.backgroundColor = swatchData.color;
      } else {
        btn.textContent = variant.title.substring(0, 2);
        btn.classList.add('swatch-btn--text-fallback');
      }

      // Preselect default if available
      if (variant.title === defaultVariantTitle) {
        btn.classList.add('selected');
        selectedVariants[componentKey] = variant;
        hasPreselected = true;
        updateSelectionLabel(componentKey, tooltipText);
        updatePreviewImage(componentKey, variant);
      }

      btn.addEventListener('click', () => {
        const clickQty = selectedQuantities[componentKey] || 1;
        if (!variant.available || !hasEnoughStock(variant.id, clickQty)) return;
        selectSwatch(btn, variant, componentKey);
        updateSelectionLabel(componentKey, tooltipText);
      });

      container.appendChild(btn);
    });

    if (!hasPreselected && defaultVariantTitle) {
      setPickerMessage(componentKey, STRINGS.defaultOutOfStock || '');
    }

    setupSwatchesToggle(container);
  }

  // Reveal the "Vis alle"/"Vis færre" button only when swatches overflow the
  // visible 2-row area. Wires up click toggle for expanding/collapsing.
  function setupSwatchesToggle(container) {
    if (!container) return;
    const body = container.closest('.bundle-color-picker__body');
    const toggle = body && body.querySelector('.swatches__toggle');
    if (!toggle) return;

    // Compare scrollHeight vs clientHeight to detect overflow (with --collapsed cap).
    const overflows = container.scrollHeight > container.clientHeight + 2;
    if (!overflows) {
      toggle.hidden = true;
      return;
    }

    toggle.hidden = false;

    // Avoid attaching multiple listeners across re-renders
    if (toggle.dataset.wired === '1') return;
    toggle.dataset.wired = '1';

    toggle.addEventListener('click', () => {
      const expanded = container.classList.toggle('swatches--expanded');
      container.classList.toggle('swatches--collapsed', !expanded);
      const collapsedLabel = toggle.querySelector('[data-toggle-collapsed]');
      const expandedLabel = toggle.querySelector('[data-toggle-expanded]');
      if (collapsedLabel) collapsedLabel.hidden = expanded;
      if (expandedLabel) expandedLabel.hidden = !expanded;
    });
  }

  // Render opskrift product card (DOM API only — no string concat to avoid XSS)
  function renderOpskriftCard(componentKey, product, variant) {
    const container = document.getElementById(componentKey + '-card');
    if (!container) return;

    container.textContent = '';

    const inner = document.createElement('div');
    inner.className = 'opskrift-card__inner';

    if (product.featured_image) {
      const img = document.createElement('img');
      img.className = 'opskrift-card__image';
      img.src = product.featured_image.replace(/\.([a-z]+)$/i, '_300x.$1');
      img.alt = product.title || '';
      img.loading = 'lazy';
      inner.appendChild(img);
    }

    const info = document.createElement('div');
    info.className = 'opskrift-card__info';

    const title = document.createElement('p');
    title.className = 'opskrift-card__title';
    title.textContent = product.title;
    info.appendChild(title);

    const price = document.createElement('p');
    price.className = 'opskrift-card__price';
    price.textContent = formatMoney(variant.price);
    info.appendChild(price);

    inner.appendChild(info);
    container.appendChild(inner);
  }

  // Update the selection label next to the component label
  function updateSelectionLabel(componentKey, displayText) {
    const picker = document.getElementById(componentKey + '-picker');
    if (!picker) return;
    let labelEl = picker.querySelector('.product-bundle__selection');
    if (!labelEl) {
      const headingLabel = picker.querySelector('.product-bundle__label');
      if (!headingLabel) return;
      labelEl = document.createElement('span');
      labelEl.className = 'product-bundle__selection';
      headingLabel.appendChild(labelEl);
    }
    labelEl.textContent = displayText ? ' · ' + displayText : '';
  }

  // Show or clear a stock-warning message inside a picker
  function setPickerMessage(componentKey, message) {
    const picker = document.getElementById(componentKey + '-picker');
    if (!picker) return;
    const existing = picker.querySelector('.swatch-message');
    if (!message) {
      if (existing) existing.remove();
      return;
    }
    if (existing) {
      existing.textContent = message;
      return;
    }
    const msg = document.createElement('p');
    msg.className = 'swatch-message';
    msg.textContent = message;
    const swatchesEl = picker.querySelector('.swatches');
    if (swatchesEl) {
      swatchesEl.parentNode.insertBefore(msg, swatchesEl.nextSibling);
    } else {
      picker.appendChild(msg);
    }
  }

  // Handle swatch selection
  function selectSwatch(btn, variant, componentKey) {
    const container = btn.parentNode;
    container.querySelectorAll('.swatch-btn').forEach((b) => b.classList.remove('selected'));

    setPickerMessage(componentKey, null);

    btn.classList.add('selected');
    selectedVariants[componentKey] = variant;

    updatePreviewImage(componentKey, variant);
    updatePrice();
    updateAddToCartButton();
    updateShareUrl();
  }

  // Open a centered overlay showing the given image at near-full-viewport size.
  // Lazy-creates the overlay on first call and reuses it for subsequent zooms.
  function openImageLightbox(src, alt) {
    let overlay = document.getElementById('bundle-preview-lightbox');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'bundle-preview-lightbox';
      overlay.className = 'bundle-preview-lightbox';
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');

      const img = document.createElement('img');
      img.className = 'bundle-preview-lightbox__image';
      img.alt = '';
      overlay.appendChild(img);

      const closeBtn = document.createElement('button');
      closeBtn.type = 'button';
      closeBtn.className = 'bundle-preview-lightbox__close';
      closeBtn.setAttribute('aria-label', STRINGS.close || 'Close');
      // Match PhotoSwipe's close SVG (snippets/icon.liquid → 'close')
      closeBtn.innerHTML = '<svg focusable="false" width="14" height="14" viewBox="0 0 14 14"><path d="M13 13L1 1M13 1L1 13" stroke="currentColor" stroke-width="2" fill="none"></path></svg>';
      closeBtn.addEventListener('click', closeImageLightbox);
      overlay.appendChild(closeBtn);

      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) closeImageLightbox();
      });

      document.body.appendChild(overlay);
    }

    const imgEl = overlay.querySelector('.bundle-preview-lightbox__image');
    imgEl.src = src;
    imgEl.alt = alt || '';

    overlay.classList.add('bundle-preview-lightbox--visible');
    document.body.style.overflow = 'hidden';

    if (!overlay._escListener) {
      overlay._escListener = (e) => {
        if (e.key === 'Escape') closeImageLightbox();
      };
      document.addEventListener('keydown', overlay._escListener);
    }
  }

  function closeImageLightbox() {
    const overlay = document.getElementById('bundle-preview-lightbox');
    if (!overlay) return;
    overlay.classList.remove('bundle-preview-lightbox--visible');
    document.body.style.overflow = '';
    if (overlay._escListener) {
      document.removeEventListener('keydown', overlay._escListener);
      overlay._escListener = null;
    }
  }

  // Make a preview thumb act as a zoom trigger. Idempotent — listeners attach once.
  function wirePreviewZoom(preview) {
    if (preview._zoomWired) return;
    preview._zoomWired = true;
    preview.addEventListener('click', () => {
      const img = preview.querySelector('img');
      if (img && img.src) openImageLightbox(img.src, img.alt);
    });
    preview.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        const img = preview.querySelector('img');
        if (img && img.src) openImageLightbox(img.src, img.alt);
      }
    });
  }

  // Update the per-component preview thumbnail with the selected variant image
  function updatePreviewImage(componentKey, variant) {
    const preview = document.getElementById(componentKey + '-preview');
    if (!preview) return;

    let src = null;
    let alt = variant && variant.title ? variant.title : '';

    if (variant && variant.featured_image && variant.featured_image.src) {
      src = variant.featured_image.src;
    } else if (variant && variant._product_title) {
      // Fall back to the product's main image (look it up via cache)
      const component = KIT.components[componentKey];
      if (component) {
        for (const handle of component.product_handles || []) {
          const prod = productCache[handle];
          if (prod && prod.title === variant._product_title && prod.featured_image) {
            src = prod.featured_image;
            break;
          }
        }
      }
    }

    if (!src) {
      const existingImg = preview.querySelector('img');
      if (existingImg) existingImg.remove();
      preview.classList.add('bundle-color-picker__preview--empty');
      preview.classList.remove('bundle-color-picker__preview--clickable');
      preview.removeAttribute('role');
      preview.removeAttribute('tabindex');
      preview.removeAttribute('aria-label');
      return;
    }

    preview.classList.remove('bundle-color-picker__preview--empty');
    let img = preview.querySelector('img');
    if (!img) {
      img = document.createElement('img');
      img.loading = 'lazy';
      preview.appendChild(img);
    }
    img.src = src;
    img.alt = alt;

    preview.classList.add('bundle-color-picker__preview--clickable');
    preview.setAttribute('role', 'button');
    preview.setAttribute('tabindex', '0');
    preview.setAttribute('aria-label', alt);
    wirePreviewZoom(preview);
  }

  // Handle size selection
  function onSizeSelected(size) {
    selectedSize = size;

    document.querySelectorAll('.size-btn').forEach((btn) => {
      const isSelected = btn.dataset.size === size;
      btn.classList.toggle('selected', isSelected);
      if (isSelected) {
        selectedBundleVariantId = parseInt(btn.dataset.variantId, 10);
      }
    });

    Object.keys(KIT.components).forEach((componentKey) => {
      const component = KIT.components[componentKey];
      const qty = (component.quantities[size] !== undefined
        ? component.quantities[size]
        : component.quantities['default']) || 1;

      const qtyEl = document.getElementById(componentKey + '-qty');
      if (qtyEl) qtyEl.textContent = qty;

      selectedQuantities[componentKey] = qty;
    });

    updateSwatchAvailability();
    updatePrice();
    updateAddToCartButton();
    updateShareUrl();
  }

  // Re-check all swatches against current required quantities
  function updateSwatchAvailability() {
    const componentsLostSelection = {};
    const seenComponents = {};

    document.querySelectorAll('.swatch-btn').forEach((btn) => {
      const componentKey = btn.dataset.componentKey;
      seenComponents[componentKey] = true;

      const variantId = parseInt(btn.dataset.variantId, 10);
      const requiredQty = selectedQuantities[componentKey] || 1;

      const variant = findCachedVariant(componentKey, variantId);
      const baseAvailable = variant ? variant.available : true;
      const enough = hasEnoughStock(variantId, requiredQty);

      if (!baseAvailable || !enough) {
        btn.style.display = 'none';

        if (btn.classList.contains('selected')) {
          btn.classList.remove('selected');
          delete selectedVariants[componentKey];
          componentsLostSelection[componentKey] = true;
        }
      } else {
        btn.style.display = '';
      }
    });

    // Clear stale messages first
    Object.keys(seenComponents).forEach((key) => setPickerMessage(key, null));

    // Add fresh warning only for components that just lost their selection
    Object.keys(componentsLostSelection).forEach((componentKey) => {
      updateSelectionLabel(componentKey, '');
      const requiredQty = selectedQuantities[componentKey] || 1;
      const template = STRINGS.notEnoughStock || '';
      setPickerMessage(componentKey, template.replace('{{ count }}', requiredQty));
    });

    // Re-evaluate the "Vis alle" toggle since hiding swatches can change overflow
    Object.keys(seenComponents).forEach((key) => {
      setupSwatchesToggle(document.getElementById(key + '-swatches'));
    });
  }

  // Find a variant in the product cache by component key and variant id
  function findCachedVariant(componentKey, variantId) {
    const component = KIT.components[componentKey];
    if (!component) return null;
    const handles = component.product_handles || [];
    for (const handle of handles) {
      const product = productCache[handle];
      if (product && product.variants) {
        const found = product.variants.find((v) => v.id === variantId);
        if (found) return found;
      }
    }
    return null;
  }

  // Calculate and display total price
  function updatePrice() {
    let total = 0;
    let itemCount = 0;

    Object.keys(selectedVariants).forEach((key) => {
      const variant = selectedVariants[key];
      const qty = selectedQuantities[key] || 1;
      if (variant) {
        total += variant.price * qty;
        itemCount += qty;
      }
    });

    const priceEl = document.getElementById('bundle-total-price');
    if (priceEl) {
      priceEl.textContent = total > 0 ? formatMoney(total) : (STRINGS.selectSizeAndColors || '');
    }

    const countEl = document.getElementById('bundle-item-count');
    if (countEl) {
      countEl.textContent = itemCount > 0 ? itemCount : '–';
    }
  }

  // Enable/disable add to cart button
  function updateAddToCartButton() {
    const btn = document.getElementById('bundle-add-to-cart');
    if (!btn) return;

    const requiredKeys = Object.keys(KIT.components).filter(
      (k) => KIT.components[k].role !== 'opskrift'
    );
    const allSelected = requiredKeys.every((key) => selectedVariants[key]);

    if (selectedSize && allSelected) {
      btn.disabled = false;
      btn.textContent = STRINGS.addToCart || '';
    } else {
      btn.disabled = true;
      btn.textContent = STRINGS.selectSizeAndColors || '';
    }
  }

  // Add bundle to cart — bundle product as parent, components as children
  async function addBundleToCart() {
    const btn = document.getElementById('bundle-add-to-cart');
    btn.disabled = true;
    btn.textContent = STRINGS.adding || '';
    showError(null);

    if (!selectedBundleVariantId) {
      btn.disabled = false;
      btn.textContent = STRINGS.selectSizeAndColors || '';
      return;
    }

    const bundleTitle = KIT.productTitle || 'Bundle Kit';
    const bundleId = Date.now().toString(); // unique ID to group items together

    const items = [
      {
        id: selectedBundleVariantId,
        quantity: 1,
        properties: {
          _bundle_id: bundleId,
          _bundle_role: 'parent',
        },
      },
    ];

    Object.keys(selectedVariants).forEach((key) => {
      const variant = selectedVariants[key];
      const qty = selectedQuantities[key] || 1;
      if (!variant) return;

      items.push({
        id: variant.id,
        quantity: qty,
        properties: {
          _bundle_id: bundleId,
          _bundle_role: 'child',
          _bundle_parent: bundleTitle,
          _bundle_component: key,
          'Del af kit': bundleTitle,
          'Type': key.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()),
        },
      });
    });

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (res.ok) {
        window.location.href = '/cart';
      } else {
        const error = await res.json();
        showError(error.description || STRINGS.addError || '');
        btn.disabled = false;
        btn.textContent = STRINGS.addToCart || '';
      }
    } catch (e) {
      showError(STRINGS.addError || '');
      btn.disabled = false;
      btn.textContent = STRINGS.addToCart || '';
    }
  }

  // Initialize bundle
  async function initBundle() {
    if (!KIT.components) return;

    const componentKeys = Object.keys(KIT.components);
    const handles = [];

    componentKeys.forEach((key) => {
      const comp = KIT.components[key];
      (comp.product_handles || []).forEach((handle) => {
        if (handle && !handles.includes(handle)) {
          handles.push(handle);
        }
      });
    });

    await Promise.all(handles.map((handle) => fetchProduct(handle)));

    componentKeys.forEach((key) => {
      const component = KIT.components[key];
      const compHandles = component.product_handles || [];

      if (component.role === 'opskrift') {
        const firstProduct = productCache[compHandles[0]];
        if (firstProduct && firstProduct.variants && firstProduct.variants.length > 0) {
          const defaultTitle = KIT.defaultSelections && KIT.defaultSelections[key];
          const opskriftVariant = (defaultTitle && firstProduct.variants.find((v) => v.title === defaultTitle))
            || firstProduct.variants.find((v) => v.available)
            || firstProduct.variants[0];
          selectedVariants[key] = opskriftVariant;
          selectedQuantities[key] = 1;
          renderOpskriftCard(key, firstProduct, opskriftVariant);
        }
        return;
      }

      // Merge variants from all products in this component, attach product title
      const allVariants = [];
      compHandles.forEach((handle) => {
        const prod = productCache[handle];
        if (prod && prod.variants) {
          prod.variants.forEach((v) => {
            allVariants.push(Object.assign({}, v, { _product_title: prod.title }));
          });
        }
      });

      if (allVariants.length > 0) {
        renderSwatches(allVariants, key + '-swatches', key);
      }
    });

    document.querySelectorAll('.size-btn').forEach((btn) => {
      btn.addEventListener('click', () => onSizeSelected(btn.dataset.size));
    });

    // Read shared URL params (?size=...&{componentKey}=variantTitle) and apply
    // them. If a param is missing for a component, the metaobject default
    // remains in effect.
    const urlParams = new URLSearchParams(window.location.search);
    const urlSize = urlParams.get('size');
    const sizeBtnFromUrl = urlSize && document.querySelector('.size-btn[data-size="' + CSS.escape(urlSize) + '"]');
    if (sizeBtnFromUrl) {
      onSizeSelected(urlSize);
    } else {
      const firstSizeBtn = document.querySelector('.size-btn');
      if (firstSizeBtn) onSizeSelected(firstSizeBtn.dataset.size);
    }

    applyUrlVariantParams(urlParams);

    const addBtn = document.getElementById('bundle-add-to-cart');
    if (addBtn) addBtn.addEventListener('click', addBundleToCart);

    const shareBtn = document.getElementById('bundle-share');
    if (shareBtn) shareBtn.addEventListener('click', shareBundle);

    updatePrice();
    updateAddToCartButton();
    updateShareUrl();
  }

  // Override default swatch selection with URL-specified variant titles.
  function applyUrlVariantParams(urlParams) {
    let anySharedOutOfStock = false;

    Object.keys(KIT.components).forEach((componentKey) => {
      const wantedTitle = urlParams.get(componentKey);
      if (!wantedTitle) return;

      const swatch = document.querySelector(
        '.swatch-btn[data-component-key="' + CSS.escape(componentKey) + '"]'
        + '[data-variant-title="' + CSS.escape(wantedTitle) + '"]'
      );

      if (!swatch || swatch.style.display === 'none') {
        anySharedOutOfStock = true;
        return;
      }

      const variantId = parseInt(swatch.dataset.variantId, 10);
      const variant = findCachedVariant(componentKey, variantId);
      if (!variant) return;

      selectSwatch(swatch, variant, componentKey);
      const label = swatch.dataset.displayLabel || variant.title;
      updateSelectionLabel(componentKey, label);
      updatePreviewImage(componentKey, variant);
    });

    if (anySharedOutOfStock) {
      showShareToast(STRINGS.sharedColorOutOfStock || '');
    }
  }

  // Build a sharable URL from current selections and write it to the address
  // bar without reloading.
  function updateShareUrl() {
    const params = new URLSearchParams();
    if (selectedSize) params.set('size', selectedSize);
    Object.keys(selectedVariants).forEach((key) => {
      const v = selectedVariants[key];
      if (v && v.title) params.set(key, v.title);
    });
    const query = params.toString();
    const newUrl = window.location.pathname + (query ? '?' + query : '') + window.location.hash;
    history.replaceState(null, '', newUrl);
  }

  // Native share via the Web Share API (opens iOS/Android share sheet,
  // Messenger, WhatsApp, SMS, email, etc.). Falls back to clipboard copy.
  async function shareBundle() {
    const url = window.location.href;
    const title = KIT.productTitle || '';
    const text = (STRINGS.shareText || '').replace('{{ title }}', title);

    if (navigator.share) {
      try {
        await navigator.share({ url: url, title: title, text: text });
        return;
      } catch (e) {
        // User cancelled or share failed — fall through to clipboard
        if (e && e.name === 'AbortError') return;
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      showShareToast(STRINGS.copiedToClipboard || '');
    } catch (e) {
      window.prompt(STRINGS.copiedToClipboard || '', url);
    }
  }

  function showShareToast(message) {
    if (!message) return;
    let toast = document.getElementById('bundle-share-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'bundle-share-toast';
      toast.className = 'bundle-share-toast';
      toast.setAttribute('role', 'status');
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.classList.add('bundle-share-toast--visible');
    clearTimeout(toast._timer);
    toast._timer = setTimeout(() => {
      toast.classList.remove('bundle-share-toast--visible');
    }, 2200);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBundle);
  } else {
    initBundle();
  }
})();
